import React, { useState, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { initialPath } from '~misc/paths'
import Select from '~components/Form/components/Select'
import { CleanFiltersIcon } from '~misc/icons'
import { useConfigureFooter } from '~hooks/useHelpers'
// import FunnelListingTabs from '../components/shared/FunnelListingTabs'
import FunnelMassDateButton from '../components/shared/FunnelMassDateButton'
import NegociacoesFuturas from '../components/Negotiations/FutureNegotiations'
import NegotiationsWorksList from '../components/Negotiations/NegotiationsWorksList'
import RenovarMassivoModal from '../components/shared/RenovarMassivoModal'
import useFunnelRowEdits from '../hooks/useFunnelRowEdits'
import useFunnelUnsavedChanges from '../hooks/useFunnelUnsavedChanges'
import { buildSelectionMap, selectedIdsFromMap } from '../helpers/selection'

const productOptions = [
  { value: 'todos', label: 'Todos' },
  { value: 'concreto', label: 'Concreto' },
  { value: 'cimentos', label: 'Cimentos' },
  { value: 'basicasEnsacados', label: 'Básicas Ensacados' },
  { value: 'basicasGranel', label: 'Básicas Granel' },
  { value: 'colantes', label: 'Colantes' },
  { value: 'agregados', label: 'Agregados' },
]

const dateOptions = [
  { value: 'maisRecente', label: 'Mais Recente' },
  { value: 'maisAntigo', label: 'Mais Antigo' },
]


const productGroupOptions = [
  { value: 'todos', label: 'Todos' },
  { value: 'concreto', label: 'Concreto' },
  { value: 'cimentos', label: 'Cimentos' },
  { value: 'basicasEnsacados', label: 'Básicas Ensacados' },
  { value: 'basicasGranel', label: 'Básicas Granel' },
  { value: 'colantes', label: 'Colantes' },
  { value: 'agregados', label: 'Agregados' },
]

const statusOptions = [
  { value: 'emNegociacao', label: 'Em Negociação' },
  { value: 'negociado', label: 'Negociado' },
  { value: 'faturado', label: 'Faturado' },
]

const mockChartData = [
  { label: 'Ago', volumeNegociado: 1000,   qtdObras: 1  },
  { label: 'Set', volumeNegociado: 1250,   qtdObras: 2  },
  { label: 'Out', volumeNegociado: 1400,  qtdObras: 6  },
  { label: 'Nov', volumeNegociado: 8000,  qtdObras: 14 },
  { label: 'Dez', volumeNegociado: 23000, qtdObras: 33 },
  { label: 'Jan', volumeNegociado: 15000, qtdObras: 21 },
]

const mockWorksList = [
  {
    id: 'w1',
    proximaNegociacao: '22/08',
    volumeTotal: '300t',
    ultimoStatus: 'Negociação no Passado',
    isPast: true,
    items: [
      { id: 'r1', clienteCodigo: 'I2I5678', produto: 'Básicas', sku: 'Assentamento', volume: 300, status: 'emNegociacao', checked: true },
    ],
  },
  {
    id: 'w2',
    proximaNegociacao: '30/09',
    volumeTotal: '1400t',
    ultimoStatus: 'Em Negociação',
    isPast: false,
    items: [
      { id: 'r2', clienteCodigo: 'I2I5678', produto: 'Concreto', sku: 'HIMIX', volume: 1400, status: 'emNegociacao', checked: false },
    ],
  },
]

const FunnelClientNextNegotiations = () => {
  const { parameter } = useParams()
  const [productGroup, setProductGroup] = useState('')
  const [product, setProduct] = useState('')
  const [dateOrder, setDateOrder] = useState('')
  const [selected, setSelected] = useState(() =>
    buildSelectionMap(mockWorksList.flatMap((w) => w.items))
  )
  const [editOpen, setEditOpen] = useState(false)

  const { edits, isDirty, setRowEdit, resetRow, clearAll } = useFunnelRowEdits(
    `funil-cliente-proximas-negociacoes-edits:${parameter}`
  )

  useFunnelUnsavedChanges(isDirty, undefined, clearAll)

  const toggleSelected = useCallback(
    (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] })),
    []
  )

  const handleSelectAll = useCallback((ids, value) => {
    setSelected((prev) => {
      const next = { ...prev }
      ids.forEach((id) => { next[id] = value })
      return next
    })
  }, [])

  const selectedIds = selectedIdsFromMap(selected)

  const allItems = useMemo(
    () => mockWorksList.flatMap((w) => w.items),
    []
  )

  const selectedRows = useMemo(
    () => allItems.filter((item) => !!selected[item.id]),
    [allItems, selected]
  )

  const handleConfirm = () => {
    setSelected({})
    clearAll()
  }

  useConfigureFooter(
    {
      buttonsComponent: selectedIds.length > 0
        ? <FunnelMassDateButton onClick={() => setEditOpen(true)} count={selectedIds.length} />
        : null,
    },
    [selectedIds.length]
  )

  const handleClear = () => {
    setProductGroup('')
    setProduct('')
    setDateOrder('')
  }

  const filteredWorks = useMemo(() => {
    let result = mockWorksList
      .map((work) => {
        const filteredItems = work.items.filter((item) => {
          const groupLabel = productGroupOptions.find((o) => o.value === productGroup)?.label ?? ''
          const matchGroup =
            !productGroup || productGroup === 'todos'
              ? true
              : item.produto.toLowerCase().includes(groupLabel.toLowerCase()) ||
                groupLabel.toLowerCase().includes(item.produto.toLowerCase())

          const productLabel = productOptions.find((o) => o.value === product)?.label ?? ''
          const matchProduct =
            !product || product === 'todos'
              ? true
              : item.sku.toLowerCase().includes(productLabel.toLowerCase()) ||
                productLabel.toLowerCase().includes(item.sku.toLowerCase())

          return matchGroup && matchProduct
        })
        return { ...work, items: filteredItems }
      })
      .filter((work) => work.items.length > 0)

    if (dateOrder) {
      result = [...result].sort((a, b) => {
        const parseDate = (str) => {
          const [day, month] = str.split('/').map(Number)
          return month * 100 + day
        }
        const da = parseDate(a.proximaNegociacao)
        const db = parseDate(b.proximaNegociacao)
        return dateOrder === 'maisRecente' ? db - da : da - db
      })
    }

    return result
  }, [productGroup, product, dateOrder])

  const navs = [
    { name: 'Próximas Negociações', link: `${initialPath}/funil-de-obras/${parameter}/proximas-negociacoes` },
    { name: 'Obras Ativas',         link: `${initialPath}/funil-de-obras/${parameter}` },
  ]

  return (
    <>
      {/* <FunnelListingTabs navs={navs} showSearch onSearch={() => {}} placeholder="Buscar Cliente" /> */}

      <div className="container mt-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <Select
              label="Grupo de Produto:"
              name="productGroup"
              options={productGroupOptions}
              value={productGroupOptions.find((o) => o.value === productGroup) ?? null}
              onChange={(opt) => setProductGroup(opt?.value ?? '')}
              placeholder="Selecione"
               spacingControl={true}
            />
          </div>
          <div className="flex-1">
            <Select
              label="Produto:"
              name="product"
              options={productOptions}
              value={productOptions.find((o) => o.value === product) ?? null}
              onChange={(opt) => setProduct(opt?.value ?? '')}
              placeholder="Selecione"
               spacingControl={true}
            />
          </div>
          <div className="flex-1">
            <Select
              label="Data de Negociação:"
              name="dateOrder"
              options={dateOptions}
              value={dateOptions.find((o) => o.value === dateOrder) ?? null}
              onChange={(opt) => setDateOrder(opt?.value ?? '')}
              placeholder="Selecione"
               spacingControl={true}
            />
          </div>
        </div>
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center gap-2 text-sm font-semibold text-primaria"
          >
            <span>Limpar Filtros</span>
            <CleanFiltersIcon />
          </button>
        </div>
      </div>
      <NegociacoesFuturas
        data={mockChartData}
        productGroupOptions={productGroupOptions}
        statusOptions={statusOptions}
        hideFilters
      />
      <NegotiationsWorksList
        works={filteredWorks}
        statusOptions={statusOptions}
        selected={selected}
        onToggle={toggleSelected}
        onSelectAll={handleSelectAll}
        onEditRow={() => setEditOpen(true)}
        edits={edits}
        onRowEdit={setRowEdit}
        onRowReset={resetRow}
      />
      <RenovarMassivoModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onSubmit={handleConfirm}
        selectedRows={selectedRows}
      />
    </>
  )
}

export default FunnelClientNextNegotiations
